import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { dateUtils } from '@makemydeal/dr-common-utils';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import { featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';

const DEAL_LOCKING_MESSAGES = {
    DIALOG_TITLE: 'Deal Finalized',
    BANNER_TITLE: 'Deal Finalized'
} as const;

/**
 * Deal Finalization Locking Hook
 *
 * This is the single source of truth for all deal locking logic based on:
 * - Feature toggle: 'lockFinalizedDeals'
 * - Dealer toggle: 'lockRestrictedDeals' (from dealerSelectors)
 * - Deal dates: finalizedDate, unwoundDate (from dealLifecycleSelectors)
 *
 * Handles deal locking logic for disabling input fields and other UI interactions
 * when deals are finalized and should be locked for editing.
 */

interface IDealLockingMessages {
    dialog: {
        title: string;
        message: string;
    };
    banner: {
        title: string;
        message: string;
    };
}
interface IDealLockingProps {
    lockFinalizedDeals: boolean;
    lockRestrictedDeals: boolean;
    finalizedDate?: string | null;
    unwoundDate?: string | null;
}

export interface IDealLockingResult extends IDealLockingMessages {
    isLocked: boolean;
}

/**
 * Determines if a deal should be locked based on feature toggles and deal dates
 */
const isDealLocked = (props: IDealLockingProps): boolean => {
    const { lockFinalizedDeals, lockRestrictedDeals, finalizedDate, unwoundDate } = props;

    // If feature toggles is off - deal is unlocked
    if (!lockFinalizedDeals || !lockRestrictedDeals) {
        return false;
    }

    const finalizedTime = finalizedDate ? new Date(finalizedDate).getTime() : null;
    const unwoundTime = unwoundDate ? new Date(unwoundDate).getTime() : null;

    // Case 1: No finalized date - always unlocked
    if (!finalizedTime) {
        return false;
    }

    // Case 2: Finalized date exists, no unwound date - locked
    if (!unwoundTime) {
        return true;
    }

    // Case 3: Both dates exist - compare timestamps
    // Deal is locked only if finalized date is more recent than unwound date
    return finalizedTime > unwoundTime;
};

/**
 * Gets the appropriate locking messages for dialog and banner
 */
const getDealLockingMessages = (isLocked: boolean, finalizedDate: string | undefined): IDealLockingMessages => {
    if (!isLocked || !finalizedDate) {
        return {
            dialog: { title: '', message: '' },
            banner: { title: '', message: '' }
        };
    }

    const formattedDate = dateUtils.getFormatDate(finalizedDate);

    return {
        dialog: {
            title: DEAL_LOCKING_MESSAGES.DIALOG_TITLE,
            message: `This deal was finalized ${formattedDate}.`
        },
        banner: {
            title: DEAL_LOCKING_MESSAGES.BANNER_TITLE,
            message: `This deal was finalized on ${formattedDate} and is now locked to prevent accidental changes. To see more details or make changes go to your DMS.`
        }
    };
};

/**
 *
 * This hook provides all the necessary information.
 * Deal locking behavior throughout the application.
 *
 * @returns {IDealLockingResult} Complete deal locking state and utility functions
 */

export const useDealFinalizationLocking = (): IDealLockingResult => {
    const lockFinalizedDeals = useSelector(featureToggleSelectors.useLockFinalizedDeals);
    const lockRestrictedDeals = useSelector(dealerSelectors.useLockRestrictedDeals);
    const dealLifecycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const { finalizedDate, unwoundDate } = dealLifecycle || { finalizedDate: null, unwoundDate: null };

    // Memoize the results to avoid unnecessary recalculations
    const result = useMemo((): IDealLockingResult => {
        const isLocked = isDealLocked({
            lockFinalizedDeals,
            lockRestrictedDeals,
            finalizedDate,
            unwoundDate
        });
        const messages = getDealLockingMessages(isLocked, finalizedDate);
        return {
            isLocked,
            dialog: messages.dialog,
            banner: messages.banner
        };
    }, [lockFinalizedDeals, lockRestrictedDeals, finalizedDate, unwoundDate]);

    return result;
};

/**
 * Convenience hook for just checking if deal is locked
 */
export const useIsDealLocked = (): boolean => {
    const { isLocked } = useDealFinalizationLocking();
    return isLocked;
};
